import {Component, forwardRef, Input, OnChanges} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import {countInvalidFields} from '../form-messages';
import {LiltDomain, LiltModel} from '../../../lilt-domains.types';

/**
 * The destination's own format_specific_data keys: `thales_fastpath_airline` for the Thales
 * fast-path destination and the Lilt item fields. Driven by `po_destination` only -- what the
 * template takes comes from its schema (SchemaFormatSpecificComponent), never from its name.
 * DefaultPoComponent merges this value into format_specific_data after the schema form's output,
 * so these keys are never stripped as undeclared.
 */
@Component({
  selector: 'app-default-format-specific',
  templateUrl: './default-format-specific.component.html',
  styleUrls: ['./default-format-specific.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DefaultFormatSpecificComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DefaultFormatSpecificComponent,
      multi: true
    }
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
  ]
})
export class DefaultFormatSpecificComponent implements ControlValueAccessor, OnChanges {

  @Input() poDestination: string;
  // Lilt domain chosen at PO level. The item picks its target languages from
  // this domain's models. Null until the operator picks one.
  @Input() liltDomain: LiltDomain | null = null;

  form: FormGroup;

  /** Set by the PO form once a submit was refused: from then on invalid fields show as such. */
  @Input()
  submitted = false;

  /** Whether a control shows its error: invalid, and touched or after a refused submit. */
  showInvalid(name: string): boolean {
    const c = this.form?.controls[name];
    return !!c && c.invalid && (c.touched || c.dirty || this.submitted);
  }

  // Models in the input domain that match our locked source language (en-US).
  // Recomputed whenever the input domain changes.
  liltAvailableModels: LiltModel[] = [];

  changed: any = () => {
  };
  touched: any = () => {
  };

  constructor(private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({});
    this.form.valueChanges.subscribe((val) => {
      if (this.touched) {
        this.touched();
      }
      if (this.changed) {
        this.changed(val);
      }
    });
  }

  writeValue(value: any): void {
    if (value) {
      this.form.patchValue(value, {emitEvent: false});
    }
  }

  registerOnChange(fn: any): void {
    this.changed = fn;
  }

  registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }

  validate() {
    return !this.form.valid && {invalid: true, invalidFields: countInvalidFields(this.form)};
  }

  ngOnChanges() {
    if (this.poDestination === 'thales_fastpath') {
      if (!('thales_fastpath_airline' in this.form.controls)) {
        this.form.addControl('thales_fastpath_airline', new FormControl('', [Validators.required]));
      }
    } else {
      this.form.removeControl('thales_fastpath_airline');
    }

    // Lilt translation destination — domain/src-lang are PO-level (see DefaultPoComponent).
    // This item just picks target languages from the chosen domain's models + optional refs.
    if (this.poDestination === 'lilt') {
      if (!('lilt_language_pairs' in this.form.controls)) {
        this.form.addControl('lilt_language_pairs', new FormControl([], [
          (control) => (!control.value || control.value.length === 0) ? {required: true} : null
        ]));
      }
      if (!('lilt_reference_urls' in this.form.controls)) {
        this.form.addControl('lilt_reference_urls', new FormControl('', [
          (control) => {
            const lines = (control.value || '').split('\n').map((l: string) => l.trim()).filter((l: string) => l !== '');
            const bad = lines.filter((l: string) => !/^s3:\/\/[^/]+\/.+/.test(l));
            return bad.length ? {invalidS3Url: bad} : null;
          }
        ]));
      }
      this.recomputeAvailableModels();
    } else {
      this.form.removeControl('lilt_language_pairs');
      this.form.removeControl('lilt_reference_urls');
    }
  }

  private recomputeAvailableModels(): void {
    // Filter to source = en-US (current locked source language).
    this.liltAvailableModels = (this.liltDomain?.models ?? []).filter(
      m => m.src_lang === 'en' && m.src_locale === 'US'
    );
    // Drop language-pair selections that no longer match the (possibly changed) domain.
    const pairsCtrl = this.form.controls['lilt_language_pairs'];
    if (pairsCtrl) {
      const validIds = new Set(this.liltAvailableModels.map(m => m.memory_id));
      const filtered = (pairsCtrl.value || []).filter((p: any) => validIds.has(p.memoryId));
      if (filtered.length !== (pairsCtrl.value || []).length) {
        pairsCtrl.setValue(filtered);
      }
    }
  }

  // Template helpers for the target-language multi-select.
  isModelSelected(model: LiltModel): boolean {
    const pairs = this.form.controls['lilt_language_pairs']?.value || [];
    return pairs.some((p: any) => p.memoryId === model.memory_id);
  }

  toggleModel(model: LiltModel): void {
    const ctrl = this.form.controls['lilt_language_pairs'];
    if (!ctrl) return;
    const pairs = [...(ctrl.value || [])];
    const idx = pairs.findIndex((p: any) => p.memoryId === model.memory_id);
    if (idx >= 0) {
      pairs.splice(idx, 1);
    } else {
      pairs.push({
        trgLang: model.trg_lang,
        trgLocale: model.trg_locale,
        memoryId: model.memory_id,
      });
    }
    ctrl.setValue(pairs);
  }

  formatModelLabel(model: LiltModel): string {
    return model.trg_locale ? `${model.trg_lang}-${model.trg_locale}` : model.trg_lang;
  }
}
